/* eslint-disable prefer-const */
import { toast } from 'react-toastify';
import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import * as XLSX from 'xlsx';
import striptags from 'striptags';
import { decryptResponse } from '@/services/apis/http-server';

dayjs.extend(utc);
dayjs.extend(timezone);

export function deBounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

export function mapModifiers(
  baseClassName: string,
  ...modifiers: (string | string[] | false | undefined)[]
): string {
  return modifiers
    .reduce<string[]>(
      (acc, m) => (!m ? acc : [...acc, ...(typeof m === 'string' ? [m] : m)]),
      [],
    )
    .map((m) => `-${m}`)
    .reduce<string>(
      (classNames, suffix) => `${classNames} ${baseClassName}${suffix}`,
      baseClassName,
    );
}

export function notify(
  type: 'info' | 'success' | 'warning' | 'error',
  message: string,
  position?:
    | 'top-right'
    | 'top-center'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-center'
    | 'bottom-left'
    | undefined,
  duration?: number,
) {
  const props = {
    position: position ?? 'top-right',
    autoClose: duration ?? 4000,
  };
  switch (type) {
    case 'info':
      return toast.info(message, props);
    case 'warning':
      return toast.warning(message, props);
    case 'error':
      return toast.error(message, props);
    default:
      return toast.success(message, props);
  }
}

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidPassword = (password: string): boolean => {
  const passwordRegex = /^(?=.*[A-Z])(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
  return passwordRegex.test(password);
};

export const isComparePassword = (
  password: string,
  confirm: string,
): boolean => {
  return password === confirm;
};

export const formatDateStringToLocalVN = (
  dateString: string,
  formatType: string = 'DD/MM/YYYY',
) => {
  return (
    dayjs(dateString)
      // .tz('Asia/Ho_Chi_Minh')
      .format(formatType)
  );
};

export const slugify = (value: string) => {
  if (!value) return '';
  const slug = `${value}`
    .toLowerCase()
    .replace(/[&/\\#"''';,+()$~%.'":*?<>{}@!^`=\][_]/g, '')
    .replace(/ /g, '-')
    .replace(/---/g, '-')
    .replace(/--/g, '-')
    .replace(/[àáạảãâầấậẩẫăằắặẳẵ]/g, 'a')
    .replace(/[èéẹẻẽêềếệểễ]/g, 'e')
    .replace(/[ìíịỉĩ]/g, 'i')
    .replace(/[òóọỏõôồốộổỗơờớợởỡ]/g, 'o')
    .replace(/[ùúụủũưừứựửữ]/g, 'u')
    .replace(/[ỳýỵỷỹ]/g, 'y')
    .replace(/đ/g, 'd');
  return slug;
};

export const slugifyFileName = (value: string) => {
  if (!value) return '';
  const slug = `${value}`
    .toLowerCase()
    .replace(/[&/\\#"''';,+$~%.'":*?<>{}@!^`=\][_]/g, '')
    .replace(/ /g, '-')
    .replace(/---/g, '-')
    .replace(/--/g, '-')
    .replace(/[àáạảãâầấậẩẫăằắặẳẵ]/g, 'a')
    .replace(/[èéẹẻẽêềếệểễ]/g, 'e')
    .replace(/[ìíịỉĩ]/g, 'i')
    .replace(/[òóọỏõôồốộổỗơờớợởỡ]/g, 'o')
    .replace(/[ùúụủũưừứựửữ]/g, 'u')
    .replace(/[ỳýỵỷỹ]/g, 'y')
    .replace(/đ/g, 'd');
  return slug;
};

export const updateURLWhenSearch = (param: string, key: string) => {
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    if (param) {
      url.searchParams.set(key, param);
    } else {
      url.searchParams.delete(key);
    }
    window.history.replaceState({}, '', url);
  }
};

export const getQueryParam = (param: string) => {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    return params.get(param);
  }
  return null;
};

export const removeHTMLTags = (htmlString: string) => {
  return striptags(htmlString);
};

export const scrollToElement = (element: HTMLElement | null) => {
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

export const removeUndefinedKeys = (obj: Record<string, any>) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined),
  );
};

export const generateRangeColor = (
  data: { value: string | number | undefined }[],
) => {
  if (!data || data?.length === 0) return [];

  const values = data
    .map((item) => Number(item?.value))
    .filter((value) => !isNaN(value))
    .sort((a, b) => a - b)
    .map((value) => Number(value?.toFixed(2)));

  if (values?.length === 0) return [];

  const groupCount = 7;
  const groupSize = Math?.floor(values?.length / groupCount);
  const remainder = values?.length % groupCount;

  const colors = [
    '#C00000',
    '#FF0000',
    '#FFC000',
    '#FFFF00',
    '#92D050',
    '#00B050',
    '#00B0F0',
  ];

  if (values?.length < groupCount) {
    return values?.map((value, index) => ({
      from: value,
      to: value,
      color: colors[index] || '#000000',
    }));
  }

  const groups: number[][] = [];
  let startIndex = 0;

  for (let i = 0; i < groupCount; i++) {
    const size = i === 0 ? groupSize + remainder : groupSize;
    groups?.push(values?.slice(startIndex, startIndex + size));
    startIndex += size;
  }

  const initRangeColor = groups?.map((group, index) => {
    const from = group[0];
    const to = group[group?.length - 1];
    return { from, to, color: colors[index] || '#000000' };
  });

  return initRangeColor;
};

export const hasUUID = (slug: string) => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const last36Chars = slug.slice(-36);
  return uuidRegex.test(last36Chars);
};

export const downloadBlobPDF = (
  base64Data: any,
  fileName: string,
  isDownload: boolean = false,
): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    try {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      if (isDownload) {
        link.download = fileName;
      } else {
        link.target = '_blank';
      }

      document.body.appendChild(link);
      link.addEventListener('click', () => {
        setTimeout(() => {
          resolve(true);
        }, 0);
      });
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error during file download:', error);
      reject(false);
    }
  });
};

export const getPathFromUrl = (url: string): string => {
  const path = url.replace(/^https?:\/\/[^/]+\/(.+)$/, '$1');
  return path;
};

export const uint8ArrayToBase64 = (uint8Array: any) => {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < uint8Array.length; i += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      uint8Array.subarray(i, i + chunkSize),
    );
  }
  return window.btoa(binary);
};

export const getFileBase64 = async (key: string) => {
  try {
    const response = await fetch(
      process.env.NEXT_PUBLIC_API_URL + 'public/v2/' + key,
      {
        headers: {
          'file-api-key': process.env.NEXT_PUBLIC_SECRET_FILE || '',
        },
      },
    );
    const blob = await response.json();
    const data = await decryptResponse(blob?.data || '');

    return data;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getFileBuffer = async (key: string) => {
  const client = new S3Client({
    region: process.env.NEXT_PUBLIC_AWS_REGION,
    credentials: {
      accessKeyId: process.env.NEXT_PUBLIC_AWS_ACCESS_KEY_ID || '',
      secretAccessKey: process.env.NEXT_PUBLIC_AWS_SECRET_ACCESS_KEY || '',
    },
  });

  try {
    const command = new GetObjectCommand({
      Bucket: process.env.NEXT_PUBLIC_AWS_BUCKET_NAME,
      Key: getPathFromUrl(key),
    });

    const response = await client.send(command);
    const data = await response.Body?.transformToByteArray();

    if (!data) return null;

    return Buffer.from(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const exportToExcel = (
  columnInfo: string[],
  columnHeaders: any[],
  units: any[],
  data: any[],
  filename: string,
) => {
  try {
    const emptyRow: never[] = [];
    const combinedData = [
      [
        'CÔNG TY TNHH TƯ VẤN CHIẾN LƯỢC CHÍNH SÁCH VÀ PHÂN TÍCH DỮ LIỆU KINH TẾ VIETSTATS - Vietstats Co.,ltd',
      ],
      columnInfo,
      emptyRow,
      emptyRow,
      columnHeaders,
      units,
      ...data.map((item) => Object.values(item)),
    ];
    const ws = XLSX.utils.aoa_to_sheet(combinedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, filename + '.xlsx');
  } catch (error) {
    console.error('Lỗi khi tạo tệp Excel:', error);
  }
};

function renderTime(type: string, data: any) {
  switch (type) {
    case 'year':
      return data?.year?.toString();
    case 'month':
      return `T${data?.month}-${data?.year}`;
    case 'quarter':
      return `Q${data?.quarter}-${data?.year}`;
    default:
      return `${data?.day}-${data?.month}-${data?.year}`;
  }
}

export const parseToExport = async (
  data: any[],
  source: string,
  title: string,
  unit: string,
  isTwoUnit: boolean = false,
  isStructure: boolean = false,
  isObjectOneTime: boolean = false,
  dataType: 'year' | 'month' | 'quarter' | 'day',
  chartType: string,
) => {
  try {
    const fileName = `${slugify(title)}_${dayjs().format('YYYYMMDD_HHmmss')}`;
    let info: string[] = [''];
    let headers: any[] = [''];
    let units: any[] = [''];
    let rows: any[] = [];
    if (!isStructure && !isObjectOneTime && !isTwoUnit) {
      info = [
        `Tên chỉ số: ${title}`,
        `Nguồn: ${source ? source : 'Vietstats'}`,
        `Đơn vị: ${unit}`,
      ];
      headers = ['', 'Thời gian', 'Giá trị'];
      units = ['', 'Đơn vị', unit];
      rows = data.map((item) => ['', renderTime(dataType, item), item?.value]);
    }

    if (
      (isStructure && !isObjectOneTime && isTwoUnit) ||
      (isStructure && !isObjectOneTime && !isTwoUnit)
    ) {
      info = [
        `Tên chỉ số: ${title}`,
        `Nguồn: ${source ? source : 'Vietstats'}`,
        `Đơn vị: ${unit}`,
      ];
      const columnsName = data.map((item) => item?.name);
      const columnsUnit = data.map((item) => item?.unit);
      headers = ['', 'Thời gian', ...columnsName];
      units = ['', 'Đơn vị', ...columnsUnit];
      rows = data[0].indicatorValue.map((row: any, indexRow: number) => {
        const dataRow = data.map(
          (item) => item?.indicatorValue[indexRow].value,
        );
        return ['', renderTime(dataType, row), ...dataRow];
      });
    }

    if (!isStructure && isObjectOneTime && !isTwoUnit) {
      info = [
        `Tên chỉ số: ${title}`,
        `Nguồn: ${source ? source : 'Vietstats'}`,
        `Đơn vị: ${unit}`,
      ];
      const columnsName = data[0].indicatorValue.map(
        (item: { name: any }) => item?.name,
      );
      const columnsUnit = data[0].indicatorValue.map((item: any) => item?.unit);
      headers = ['', 'Đối tượng', ...columnsName];
      units = ['', 'Đơn vị', ...columnsUnit];
      rows = data.map((item) => {
        const data = item?.indicatorValue.map((row: any) => row?.value);
        return ['', item?.name, ...data];
      });
    }

    await exportToExcel(info, headers, units, rows, fileName);
  } catch (error) {
    console.error('Lỗi khi tạo tệp Excel:', error);
  }
};

export function clipboard(value: string, message?: string) {
  navigator?.clipboard
    ?.writeText(value || '...')
    .then(() => {
      notify(
        'success',
        message ? message : 'Copy ID thành công',
        'top-center',
        5000,
      );
    })
    .catch((error) => {
      notify('error', 'Copy thất bại', 'top-center', 5000);
    });
}

export const checkTokenExpired = (expiryTime: any) => {
  const currentTime = Math.floor(Date.now() / 1000);
  return expiryTime - currentTime <= 0;
};

export const generateGUID = () =>
  '00-0-4-1-000'.replace(/[^-]/g, (s: any) =>
    (((Math.random() + ~~s) * 0x10000) >> s).toString(16).padStart(4, '0'),
  );

export const delayLoading = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));
// await delayLoading(300000) loading 300s;

export const processSlug = (slug: string) => {
  if (hasUUID(slug)) {
    return slug.slice(0, -36).replace('28-', '').replace(/-/g, '_');
  }
  return slug.replace('28-', '').replace(/-/g, '_');
};

export function getMimeTypeFromBase64Header(base64: string): string {
  const match = base64.match(/^data:(.*?);base64,/);
  return match ? match[1] : 'application/octet-stream';
}
